/**
 * Cloudflare Turnstile CAPTCHA Component
 *
 * Privacy-friendly, invisible CAPTCHA for form protection.
 *
 * Usage:
 * ```tsx
 * import { Turnstile } from '@/components/common/Turnstile';
 *
 * function LoginForm() {
 *   const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
 *
 *   const handleSubmit = async () => {
 *     await fetch('/api/login', {
 *       method: 'POST',
 *       body: JSON.stringify({
 *         email,
 *         password,
 *         'cf-turnstile-response': turnstileToken
 *       })
 *     });
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <input type="email" ... />
 *       <input type="password" ... />
 *       <Turnstile
 *         action="login"
 *         onVerify={setTurnstileToken}
 *         onError={() => setTurnstileToken(null)}
 *       />
 *       <button disabled={!turnstileToken}>Login</button>
 *     </form>
 *   );
 * }
 * ```
 *
 * Setup:
 * 1. Get site key from Cloudflare Turnstile dashboard
 * 2. Set VITE_TURNSTILE_SITE_KEY in .env
 */

import { useEffect, useRef, useCallback, useState } from 'react';

// Turnstile site key from environment
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

// Check if Turnstile is configured
const IS_TURNSTILE_ENABLED = !!TURNSTILE_SITE_KEY;

interface TurnstileProps {
  /** Action name for analytics (e.g., 'login', 'register') */
  action?: string;
  /** Callback when verification succeeds */
  onVerify: (token: string) => void;
  /** Callback when verification fails */
  onError?: (error: string) => void;
  /** Callback when token expires */
  onExpire?: () => void;
  /** Theme: 'light', 'dark', or 'auto' */
  theme?: 'light' | 'dark' | 'auto';
  /** Size: 'normal' or 'compact' */
  size?: 'normal' | 'compact';
  /** Additional CSS class */
  className?: string;
  /** Retry on error */
  retryOnError?: boolean;
}

// Declare global turnstile object
declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'error-callback'?: (error: string) => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact';
          action?: string;
          'retry'?: 'auto' | 'never';
          'retry-interval'?: number;
        }
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

// Track if script is loading/loaded
let scriptLoadPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  // Check if already loaded
  if (window.turnstile) {
    return Promise.resolve();
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
    script.async = true;
    script.defer = true;

    window.onTurnstileLoad = () => {
      resolve();
    };

    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error('Failed to load Turnstile script'));
    };

    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

export function Turnstile({
  action,
  onVerify,
  onError,
  onExpire,
  theme = 'auto',
  size = 'normal',
  className = '',
  retryOnError = true,
}: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleVerify = useCallback(
    (token: string) => {
      setError(null);
      onVerify(token);
    },
    [onVerify]
  );

  const handleError = useCallback(
    (errorCode: string) => {
      // Always clear isLoading on error — otherwise the container stays at
      // opacity:0 and an interactive challenge (when Cloudflare escalates)
      // is invisible to the user. They see "Loading…" forever while
      // Turnstile is actually waiting on a click in the hidden iframe.
      setError(errorCode);
      setIsLoading(false);
      onError?.(`Turnstile error: ${errorCode}`);
    },
    [onError]
  );

  // Triggering a re-render via this nonce remounts the widget. Faster + less
  // disruptive than a full page reload when the user retries.
  const [retryNonce, setRetryNonce] = useState(0);
  const handleRetry = useCallback(() => {
    setError(null);
    setIsLoading(true);
    setRetryNonce((n) => n + 1);
  }, []);

  const handleExpire = useCallback(() => {
    setError(null);
    onExpire?.();
    // Reset widget on expiry
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, [onExpire]);

  useEffect(() => {
    // Skip if not configured (dev mode)
    if (!IS_TURNSTILE_ENABLED) {
      setIsLoading(false);
      // In dev mode, auto-verify with a dummy token
      if (import.meta.env.DEV) {
        onVerify('dev-mode-token');
      }
      return;
    }

    let mounted = true;

    const initTurnstile = async () => {
      try {
        await loadTurnstileScript();

        if (!mounted || !containerRef.current || !window.turnstile) {
          return;
        }

        // Remove existing widget
        if (widgetIdRef.current) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }

        // Use requestAnimationFrame to ensure the container is visible in the DOM
        // before asking Cloudflare to render the widget. This avoids issues where
        // the SDK fails to render into a container that's not yet laid out.
        await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

        if (!mounted || !containerRef.current || !window.turnstile) {
          return;
        }

        // Render widget
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: handleVerify,
          'error-callback': handleError,
          'expired-callback': handleExpire,
          theme,
          size,
          action,
          'retry': retryOnError ? 'auto' : 'never',
          'retry-interval': 5000,
        });

        setIsLoading(false);
      } catch (err) {
        if (mounted) {
          const errorMsg = err instanceof Error ? err.message : 'Failed to initialize Turnstile';
          setError(errorMsg);
          setIsLoading(false);
          onError?.(errorMsg);
        }
      }
    };

    initTurnstile();

    return () => {
      mounted = false;
      // Cleanup widget on unmount
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [action, theme, size, retryOnError, retryNonce, handleVerify, handleError, handleExpire, onError, onVerify]);

  // Not configured - show nothing in production, dev notice in dev
  if (!IS_TURNSTILE_ENABLED) {
    if (import.meta.env.DEV) {
      return (
        <div className={`text-xs text-gray-400 ${className}`}>
          [Turnstile disabled in dev mode]
        </div>
      );
    }
    return null;
  }

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {isLoading && !error && (
        <div className="flex items-center justify-center h-[65px] text-sm text-gray-500">
          Loading security check...
        </div>
      )}

      {/* Container must not use display:none (hidden) — Cloudflare's SDK cannot
          render its iframe into a hidden element. Use opacity/height to hide
          during the initial load only. Once an error has surfaced we let the
          container be visible so that an interactive challenge (escalated by
          Cloudflare when a silent verify fails) can be clicked. */}
      <div
        ref={containerRef}
        style={isLoading && !error ? { opacity: 0, height: 0, overflow: 'hidden' } : undefined}
        data-action={action}
      />

      {error && (
        <div className="w-full max-w-sm mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm space-y-2">
          <div className="font-medium text-amber-900">
            CAPTCHA could not load
          </div>
          <p className="text-amber-800 text-xs leading-relaxed">
            Cloudflare Turnstile could not start its challenge ({error}).
            Common causes:
          </p>
          <ul className="text-amber-800 text-xs space-y-0.5 ml-4 list-disc">
            <li>Browser extensions blocking inline scripts (MetaMask, NoScript, uBlock Origin)</li>
            <li>Corporate firewall blocking <code className="font-mono">challenges.cloudflare.com</code></li>
            <li>Strict tracking protection (Brave Shields, Firefox Strict mode)</li>
          </ul>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={handleRetry}
              className="px-3 py-1.5 text-xs font-medium bg-amber-600 hover:bg-amber-700 text-white rounded-md transition-colors"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-3 py-1.5 text-xs font-medium border border-amber-300 hover:bg-amber-100 text-amber-900 rounded-md transition-colors"
            >
              Reload page
            </button>
            <a
              href="mailto:support@serac.build?subject=CAPTCHA%20issue%20on%20login"
              className="px-3 py-1.5 text-xs font-medium text-amber-900 hover:underline self-center ml-auto"
            >
              Contact support
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Hook for managing Turnstile state in forms
 */
export function useTurnstile() {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);

  const handleVerify = useCallback((newToken: string) => {
    setToken(newToken);
    setError(null);
    setIsVerified(true);
  }, []);

  const handleError = useCallback((errorMsg: string) => {
    setToken(null);
    setError(errorMsg);
    setIsVerified(false);
  }, []);

  const handleExpire = useCallback(() => {
    setToken(null);
    setIsVerified(false);
  }, []);

  const reset = useCallback(() => {
    setToken(null);
    setError(null);
    setIsVerified(false);
  }, []);

  return {
    token,
    error,
    isVerified,
    // Skip verification if not configured (dev mode)
    isReady: IS_TURNSTILE_ENABLED ? isVerified : true,
    handlers: {
      onVerify: handleVerify,
      onError: handleError,
      onExpire: handleExpire,
    },
    reset,
  };
}

export default Turnstile;
